import { memo, useCallback, useEffect } from 'react';

import _ from 'lodash';

import { RichEditor } from '@/components/ui/richEditor';
import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import { getTextData } from '@/components/ui/widget/utils/widget.utils';
import { getByPath } from '@/utils/widgets';
import type { WidgetData, WidgetUpdate } from '../../widget.type';
import { CommentEditor } from './editors/comment/comment.editor';
import { DateEditor } from './editors/date/date.editor';
import type { CellEditor } from './editors/editors.types';
import { TextEditor } from './editors/input/text.editor';
import { MultiselectEditor } from './editors/multiselect/multiselect.editor';
import { NumberInputEditor } from './editors/number/number.editor';
import { SelectDropdown } from './editors/select/select.editor';
import type { TextView } from './text.view.type';

// text editor types
export const TEXT_EDITOR_INPUT = 'input' as const;
export const TEXT_EDITOR_NUMBER = 'number' as const;
export const TEXT_EDITOR_SELECT = 'select' as const;
export const TEXT_EDITOR_COMMENT = 'comment' as const;
export const TEXT_EDITOR_MULTISELECT = 'multiselect' as const;
export const TEXT_EDITOR_DATE = 'date' as const;

type Props = {
  view: TextView;
  data: WidgetData;
  update?: WidgetUpdate | null;
};

// simple text to display
const TextViewImpl = memo(({ view, data, update = null }: Props) => {
  const { path, default: defaultValue, editor, isRichText } = view;
  const { patch, onChange, setIsPatched, editorOverrides = {} } = update || {};

  const disabledPath = editor?.disabledPath;
  const disabledFromData = !!getByPath(data, disabledPath);

  const textData =
    view?.aggregationValue ?? getTextData(data, path, defaultValue);

  // if no editor and no patch, directly return a simple text view
  if (!(editor || patch)) {
    if (isRichText) {
      return <RichEditor readOnly value={textData} />;
    }
    return <div className="overflow-hidden text-ellipsis">{textData}</div>;
  }
  // if the view is editable display a specific view

  // check if the data to display has been patched
  const dataWithPatchApplied = _.merge({}, data, patch ?? {});

  const displayData = getTextData(dataWithPatchApplied, path);

  const handleChange = useCallback(
    (value: string | string[] | number | null | undefined) => {
      const isSameAsData =
        value === textData || (textData === undefined && value === ''); // handle the case where initial data is unset and the text input was cleared
      onChange?.({ path, value: isSameAsData ? undefined : value });
    },
    [path, onChange]
  );

  useEffect(() => {
    if (setIsPatched) setIsPatched(displayData !== textData);
  }, [textData, displayData, setIsPatched]);

  // if no editor but patch, just output the potential patch value
  if (!editor) {
    return <div className="overflow-hidden text-ellipsis">{displayData}</div>;
  }

  const { placeholder } = editor;
  const { header: rawHeader } = editorOverrides;
  const header = typeof rawHeader === 'string' ? rawHeader : undefined;

  const disabled =
    !!editorOverrides.disabled ||
    editor.disabled ||
    !onChange ||
    disabledFromData;

  const identifier = `${path}-${header ? 'batch' : 'input'}`;

  switch (editor.type?.toLowerCase()) {
    case TEXT_EDITOR_NUMBER: {
      const numberEditor = editor as CellEditor<'number'>;
      return (
        <NumberInputEditor
          delay={numberEditor.delay}
          disabled={disabled}
          format={numberEditor.format}
          header={header}
          identifier={identifier}
          onChange={handleChange}
          placeholder={placeholder}
          rawData={displayData || null}
          saveFormatted={numberEditor.saveFormatted}
        />
      );
    }
    case TEXT_EDITOR_SELECT: {
      const selectEditor = editor as CellEditor<'select'>;
      let { options } = selectEditor;
      if (selectEditor.optionsPath) {
        options = getByPath(dataWithPatchApplied, selectEditor.optionsPath);
      }

      return (
        <SelectDropdown
          disabled={disabled}
          header={header}
          identifier={identifier}
          onChange={handleChange}
          options={options}
          placeholder={placeholder}
          value={displayData}
        />
      );
    }
    case TEXT_EDITOR_COMMENT: {
      const commentEditor = editor as CellEditor<'comment'>;
      return (
        <CommentEditor
          delay={commentEditor.delay}
          disabled={disabled}
          header={header}
          identifier={identifier}
          onChange={handleChange}
          placeholder={placeholder}
          value={displayData || ''}
        />
      );
    }
    case TEXT_EDITOR_MULTISELECT: {
      const multiselectEditor = editor as CellEditor<'multiselect'>;
      let { options } = multiselectEditor;
      if (multiselectEditor.optionsPath) {
        options = getByPath(
          dataWithPatchApplied,
          multiselectEditor.optionsPath
        );
      }

      return (
        <MultiselectEditor
          disabled={disabled}
          header={header}
          identifier={identifier}
          onChange={handleChange}
          options={options}
          placeholder={placeholder}
          value={displayData ? JSON.parse(displayData) : []}
        />
      );
    }
    case TEXT_EDITOR_DATE: {
      const dateEditor = editor as CellEditor<'date'>;
      return (
        <DateEditor
          disabled={disabled}
          displayFormat={dateEditor.displayFormat}
          header={header}
          identifier={identifier}
          inputFormat={dateEditor.inputFormat}
          onChange={handleChange}
          placeholder={placeholder}
          value={displayData || ''}
        />
      );
    }
    default: {
      const editorInput = editor as CellEditor<'input'>;
      return (
        <TextEditor
          delay={editorInput.delay}
          disabled={disabled}
          header={header}
          identifier={identifier}
          onChange={handleChange}
          placeholder={placeholder}
          value={displayData || ''}
        />
      );
    }
  }
});

// sort defaults to text on path
const getSortConfig = (view: TextView): TextView => ({
  type: 'text',
  path: view.path
});

// filtering defaults to text search on path
const getFilterConfig = (view: TextView): TextView => ({
  type: 'text',
  path: view.path
});

// width defaults to 'medium'
const getDefaultWidth = (): ColumnWidth => 'medium';

// stringifies data at path
const getStringifyFunction = (view: TextView) => {
  const { path, default: defaultValue } = view;
  return (data: WidgetData) => getTextData(data, path, defaultValue);
};

// get edited path
// returns path if editor is set, null otherwise
const getEditedPath = (view: TextView) => {
  const { path, editor } = view;
  return editor ? path : null;
};

const getAggregationConfig = (view: TextView) => {
  const { path, aggregationValue } = view;
  if (aggregationValue) {
    return {
      view: {
        path,
        aggregationValue
      }
    };
  }
  return null;
};

export const textViewProvider = {
  component: TextViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction,
  getEditedPath,
  getAggregationConfig
};
